/**
 * PWA helper — registers the service worker exactly once and wires the
 * `beforeinstallprompt` event so we can show a custom Install prompt.
 *
 * Call `registerPwa()` once from index.js at module top-level.
 * Read the stored prompt via `getInstallPrompt()` / `clearInstallPrompt()`.
 */
let deferredPrompt = null;
const listeners = new Set();

function emit() {
  listeners.forEach((fn) => {
    try { fn(deferredPrompt); } catch { /* ignore */ }
  });
}

export function registerPwa() {
  // Service worker — register once on load
  if ("serviceWorker" in navigator) {
    window.addEventListener("load", () => {
      navigator.serviceWorker.register("/sw.js")
        .catch((err) => console.warn("SW register failed", err));
    });
  }
  // Capture install prompt
  window.addEventListener("beforeinstallprompt", (e) => {
    e.preventDefault();
    deferredPrompt = e;
    emit();
  });
  window.addEventListener("appinstalled", () => {
    deferredPrompt = null;
    try { localStorage.setItem("trp_installed", "1"); } catch { /* ignore */ }
    emit();
  });
}

export function getInstallPrompt() {
  return deferredPrompt;
}

export function subscribeInstallPrompt(fn) {
  listeners.add(fn);
  // Fire once with current state
  try { fn(deferredPrompt); } catch { /* ignore */ }
  return () => listeners.delete(fn);
}

export async function promptInstall() {
  if (!deferredPrompt) return { outcome: "unavailable" };
  try {
    deferredPrompt.prompt();
    const choice = await deferredPrompt.userChoice;
    deferredPrompt = null;
    emit();
    return choice;
  } catch (e) {
    return { outcome: "error", error: String(e) };
  }
}

export function isInstalled() {
  if (typeof window === "undefined") return false;
  if (window.matchMedia?.("(display-mode: standalone)").matches) return true;
  if (window.navigator?.standalone) return true;
  try { return localStorage.getItem("trp_installed") === "1"; } catch { return false; }
}
